import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SouthamptonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Southampton Pothole Claims | 70% of Roads Need Capital Funding | Fixtyer"
        description="Southampton City Council's own data shows 70% of roads — about 235 miles — need capital funding, with only 24% in good condition. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/southampton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Southampton', url: '/councils/southampton' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Southampton spends above its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate spending. Southampton is AMBER overall because road condition is AMBER: only 24% of the network was in good (green) condition in 2024, 66% was amber and 10% red, and the council admits roads are deteriorating each year.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 475.6km — 80% of Southampton\'s 591.7km network. Since 2022 all U-roads are surveyed annually using GAIST 360° camera imagery. In 2024, 16.16% of U-road surface area was in moderate-to-severe deterioration (Category 4) and 6.09% in severe deterioration (Category 5) — roughly 106km needing rehabilitative or structural work. Prior CVI surveys found 8–15% of U-roads in RED condition between 2020 and 2023.',
          },
          {
            question: 'Is my pothole Hampshire County Council\'s responsibility or Southampton\'s?',
            answer: 'Southampton City Council maintains 591.7km of roads within the city boundary only. Hampshire County Council maintains roads outside the city. National Highways is responsible for motorways and major A-roads including the M27, M3, M271, A3(M) and sections of the A34. If your damage was on a trunk road, report it to National Highways — not Southampton or Hampshire.',
          },
          {
            question: 'Southampton recorded 0% preventative maintenance in 2024/25 — does that help my claim?',
            answer: 'It is relevant context. The council\'s own spending table shows 0% estimated preventative maintenance and 52% reactive in 2024/25 — the same year it prepared an "emergency/statutory minimum service" Road Repairs Strategy because of budget pressures. That does not automatically prove liability for your specific defect, but it shows the council was prioritising reactive patching over structural renewal when your pothole may have formed.',
          },
          {
            question: 'Why did pothole repairs fall from 6,137 to 4,247 — does that mean the roads are fixed?',
            answer: 'No. Southampton still filled 4,247 potholes and footway defects in 2024/25 — 2,781 on roads alone. The council predicts around 4,400 fills in 2025/26. The lower total partly reflects the end of the "Find and Fix" programme that contributed 1,526 and 2,045 repairs in earlier years. Overall condition data shows the network is still deteriorating.',
          },
          {
            question: 'Southampton switched from SCANNER to GAIST surveys — can the council compare old and new data?',
            answer: 'Carefully. SCANNER laser surveys cover A and B/C roads through 2023; since 2022 GAIST 360° imagery assesses all road classes using five PAS2161-style categories. The council states this alignment allows funding to be allocated more consistently — but Category 4 "moderate to severe" on A-roads rose from 3.87% to 5.57% between 2022 and 2024, and overall the council admits "the roads in Southampton are deteriorating each year."',
          },
          {
            question: 'Do I need to report the pothole before claiming?',
            answer: 'Reporting creates a dated council record through the Southampton Highways Partnership — useful if the defect was known before your incident or left unrepaired within the council\'s agreed risk-based response times. The council categorises reactive repairs by "agreed defect and risk parameters, and response times." Keep your reference number and any confirmation emails.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Southampton City Council"
        councilSlug="southampton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/southampton"
        headline="Southampton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Southampton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Southampton: 70% of roads need capital funding now
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Southampton City Council&apos;s own transparency report states{' '}
            <strong className="text-rose-400">around 70% of the city&apos;s roads — about 235 miles — need capital
            funding</strong> for preventative, rehabilitative or structural maintenance. Only{' '}
            <strong className="text-amber-400">24% were in good condition in 2024</strong>, down 1.4% on the previous
            year, while the DfT rates overall performance <strong className="text-amber-400">AMBER</strong>, spend{' '}
            <strong className="text-emerald-400">GREEN</strong>, and best practice{' '}
            <strong className="text-amber-400">AMBER</strong>. The council filled{' '}
            <strong className="text-rose-400">4,247 potholes and footway defects in 2024/25</strong> on a network where
            it admits roads are deteriorating each year.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">70%</div>
            <div className="text-white font-medium mt-2">Of roads need capital funding (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              About 235 miles on a 591.7km network — with only 24% in good (green) condition, 66% amber and 10% red.
              The council states: &ldquo;Overall, the roads in Southampton are deteriorating each year.&rdquo;
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The condition picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Network-wide assessment and five-year survey data from Southampton City Council&apos;s own transparency
            report — SCANNER on classified roads through 2023, GAIST on all classes since 2022
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">591.7km</div>
              <div className="text-sm text-surface-600">Total road network</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-amber-700">475.6km</div>
              <div className="text-sm text-amber-800">Unclassified roads (80%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">116.1km</div>
              <div className="text-sm text-surface-600">A, B and C roads (20%)</div>
            </div>
          </div>

          {/* Overall 2024 breakdown */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Whole-network condition (2024): slipping year on year</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">24%</div>
                <div className="text-sm text-emerald-800">Green (good)</div>
                <div className="text-xs text-surface-500">down 1.4% from 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">66%</div>
                <div className="text-sm text-amber-800">Amber (fair)</div>
                <div className="text-xs text-surface-500">~235 miles — up 1.7% on 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">10%</div>
                <div className="text-sm text-rose-800">Red (poor)</div>
                <div className="text-xs text-surface-500">~36 miles need immediate funding</div>
              </div>
            </div>
            <p className="text-amber-800 font-medium">
              Three-quarters of Southampton&apos;s carriageway network is not in good condition. The council
              categorises amber roads as having &ldquo;generally deteriorated to requiring maintenance soon&rdquo;
              and red roads as having &ldquo;deteriorated to the point they need repair.&rdquo;
            </p>
          </div>

          {/* A-roads GAIST */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (58.2km — 9.8% of network): GAIST survey trend</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Cat 1 (none)</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Cat 2 (minor)</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Cat 3 (moderate)</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Cat 4 (mod–severe)</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Cat 5 (severe)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">34.48%</td>
                    <td className="px-4 py-2 text-center">7.26%</td>
                    <td className="px-4 py-2 text-center">49.95%</td>
                    <td className="px-4 py-2 text-center">3.87%</td>
                    <td className="px-4 py-2 text-center">4.40%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">29.72%</td>
                    <td className="px-4 py-2 text-center">6.73%</td>
                    <td className="px-4 py-2 text-center">54.26%</td>
                    <td className="px-4 py-2 text-center">4.03%</td>
                    <td className="px-4 py-2 text-center">5.25%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">27.61%</td>
                    <td className="px-4 py-2 text-center">8.08%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">55.94%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.57%</td>
                    <td className="px-4 py-2 text-center">2.80%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Category 1 (no deterioration) fell from 34.48% to 27.61% in two years. Category 3 moderate
              deterioration now covers 55.94% of A-road surface area. All A-roads are surveyed annually.
              Legacy SCANNER data (2020–2023) showed 67–71% green, 25–29% amber and 4–5% red — a different
              methodology, not directly comparable to GAIST categories.
            </p>
          </div>

          {/* B/C roads GAIST */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (57.9km): deteriorating under GAIST</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Cat 1</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Cat 3</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Cat 4</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Cat 5</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">31.31%</td>
                    <td className="px-4 py-2 text-center">52.26%</td>
                    <td className="px-4 py-2 text-center">4.87%</td>
                    <td className="px-4 py-2 text-center">7.01%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">26.75%</td>
                    <td className="px-4 py-2 text-center">53.67%</td>
                    <td className="px-4 py-2 text-center">5.46%</td>
                    <td className="px-4 py-2 text-center">8.72%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25.18%</td>
                    <td className="px-4 py-2 text-center font-bold">54.71%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.77%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.65%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Category 4 (moderate to severe) nearly doubled from 4.87% to 7.77% since 2022. Combined Cat 4
              and Cat 5 deterioration rose from 11.88% to 13.42%. Legacy SCANNER showed B/C roads broadly
              stable at 78–80% green and 2–3% red through 2023 — the GAIST transition reveals a steeper picture.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend — but 0% preventative in 2024/25</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.40m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.20m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">0%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Southampton spent well over its DfT allocation in 2024/25 — earning a{' '}
              <span className="text-emerald-400">GREEN Spend scorecard</span> — yet recorded{' '}
              <span className="text-rose-400">zero estimated preventative maintenance</span> and 52% reactive.
              Total highways maintenance was around £7.9m (capital plus revenue). The problem is not always
              the headline spend figure — it is what the money bought on a deteriorating network.
            </p>
          </div>
        </div>
      </section>

      {/* U-roads and survey alignment */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 476km residential network
          </h2>
          <p className="text-surface-600 mb-8">
            80% of Southampton&apos;s carriageways are unclassified — now surveyed annually via GAIST since 2022
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED (CVI survey)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Cat 4 + 5 (GAIST, road area %)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Pre-GAIST</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">8%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">Pre-GAIST</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">15%</td>
                  <td className="px-6 py-4 text-center">20.04%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                  <td className="px-6 py-4 text-center">22.10%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">GAIST only</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22.25%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">GAIST detail — U-roads 2024</h3>
              <div className="grid grid-cols-3 gap-3 mb-3 text-center text-sm">
                <div>
                  <div className="text-xl font-bold text-emerald-600">16.40%</div>
                  <div className="text-surface-600">Cat 1</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-amber-600">49.21%</div>
                  <div className="text-surface-600">Cat 3</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-rose-600">22.25%</div>
                  <div className="text-surface-600">Cat 4 + 5</div>
                </div>
              </div>
              <p className="text-surface-600 text-sm">
                Category 1 (no deterioration) fell from 25.68% in 2022 to 16.40% in 2024 — a loss of roughly
                44km of surface area in the best condition band. Category 3 moderate deterioration covers
                nearly half of all U-road surface area.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Survey alignment since 2022</h3>
              <p className="text-amber-800 text-sm mb-3">
                Since 2022 the unclassified network has been surveyed at the same time as A and B/C roads using
                360° vehicle-mounted camera imagery. The council states this &ldquo;alignment of road condition
                data has allowed the different road classifications to be assessed in the same way&rdquo; —
                but the unified picture shows deterioration accelerating, not stabilising.
              </p>
              <p className="text-amber-800 text-sm">
                Prior CVI (Course Visual Inspection) RED percentages of 8–15% used a different method and
                cannot be directly compared to GAIST categories.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Southampton must show it had a reasonable system for knowing
              road condition. On a network where 70% needs capital funding, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your street in the 10% red, 66% amber, or declining 24% green band when you were damaged?</li>
              <li>• If Cat 4 deterioration on U-roads rose from 11.22% to 16.16%, what was done about yours?</li>
              <li>• Did the council&apos;s emergency/statutory minimum service strategy leave your road for reactive patching only?</li>
              <li>• Can the council prove it acted on GAIST data showing Category 3 covering half of U-road surface area?</li>
            </ul>
            <p className="text-white font-medium">
              A council that publishes data showing 235 miles need capital funding cannot easily argue it had
              no reason to expect defects on untreated amber roads.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs and spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            23,383 defects filled in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Pothole and footway repair totals from Southampton&apos;s own transparency report — reactive work on a
            deteriorating network
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Roads</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Footways</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Find and Fix</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,273</td>
                  <td className="px-6 py-4 text-center">915</td>
                  <td className="px-6 py-4 text-center">319</td>
                  <td className="px-6 py-4 text-center font-bold">3,507</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,444</td>
                  <td className="px-6 py-4 text-center">1,648</td>
                  <td className="px-6 py-4 text-center">2,045</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,137</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,386</td>
                  <td className="px-6 py-4 text-center">1,832</td>
                  <td className="px-6 py-4 text-center">1,526</td>
                  <td className="px-6 py-4 text-center font-bold">5,744</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">2,856</td>
                  <td className="px-6 py-4 text-center">755</td>
                  <td className="px-6 py-4 text-center">137</td>
                  <td className="px-6 py-4 text-center font-bold">3,748</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">2,781</td>
                  <td className="px-6 py-4 text-center">1,466</td>
                  <td className="px-6 py-4 text-center">0</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,247</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">12,740</td>
                  <td className="px-6 py-4 text-center font-bold">6,616</td>
                  <td className="px-6 py-4 text-center font-bold">4,027</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23,383</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~13 repairs a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Southampton fills around 13 road and footway defects per day across
                its 591.7km network. The council predicts around 4,400 fills in 2025/26 (3,000 roads, 1,400
                footways). A network producing defects at this rate is one where potholes routinely form
                between inspections — exactly the scenario where prior reports and photographic evidence decide
                claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Reactive spend dominance</h3>
              <p className="text-amber-800 text-sm">
                Estimated reactive maintenance share was 46–55% across 2020/21 to 2024/25, hitting 52% in the
                latest year when preventative spend was recorded at 0%. The council states reactive maintenance
                is &ldquo;revenue funded and categorised by agreed defect and risk parameters, and response
                times&rdquo; — your claim may turn on whether those parameters were met for your specific defect.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <div className="bg-surface-100 px-6 py-3 border-b border-surface-200">
              <h3 className="font-semibold text-brand-900">Highways maintenance spending (five years)</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="px-4 py-2 text-left font-semibold">Year</th>
                  <th className="px-4 py-2 text-center font-semibold">DfT allocation</th>
                  <th className="px-4 py-2 text-center font-semibold">Capital spend</th>
                  <th className="px-4 py-2 text-center font-semibold">Revenue spend</th>
                  <th className="px-4 py-2 text-center font-semibold">Preventative</th>
                  <th className="px-4 py-2 text-center font-semibold">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">£3.24m</td>
                  <td className="px-4 py-3 text-center">£4.24m</td>
                  <td className="px-4 py-3 text-center">£3.83m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">32.75%</td>
                  <td className="px-4 py-3 text-center">51%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center">£2.40m</td>
                  <td className="px-4 py-3 text-center font-bold">£4.20m</td>
                  <td className="px-4 py-3 text-center">£3.72m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">0%</td>
                  <td className="px-4 py-3 text-center font-bold">52%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£2.78m</td>
                  <td className="px-4 py-3 text-center">£12.32m</td>
                  <td className="px-4 py-3 text-center">£3.58m</td>
                  <td className="px-4 py-3 text-center">0%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£2.13m</td>
                  <td className="px-4 py-3 text-center">£14.98m</td>
                  <td className="px-4 py-3 text-center">£3.17m</td>
                  <td className="px-4 py-3 text-center">0%</td>
                  <td className="px-4 py-3 text-center">55%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£2.13m</td>
                  <td className="px-4 py-3 text-center">£10.61m</td>
                  <td className="px-4 py-3 text-center">£2.95m</td>
                  <td className="px-4 py-3 text-center">13.2%</td>
                  <td className="px-4 py-3 text-center">54%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Emergency budget admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Second year of emergency minimum service
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own explanation for limited resurfacing — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Overall, the roads in Southampton are deteriorating each year. The length of roads in Grade 3 (Moderate deterioration) is increasing and the amount of road surface with no deterioration (i.e. Grade 1), decreasing."
              source="Southampton City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="In 2024/25 uncertainty over the capital settlement due to the pressures on the Council's budgets and lower levels of funding for highway infrastructure maintenance meant an emergency / statutory minimum service Road Repairs Strategy was used to identify maintenance priorities. This approach has been followed again when preparing the 2025/26 Annual Plan."
              source="Southampton City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="The major reductions in the Council's 2024/25 funding and an anticipated 'Low' funding level of SCC Highways Capital funding in 2025/26 meant preparing an Annual Plan against an 'emergency/statutory minimum service' budget for a second year. This funding level doesn't support the programming of larger road (or footway) resurfacing schemes during 2025/26."
              source="Southampton City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the 2025/26 plan actually delivers</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Resurface and repair 3.5 miles of roads; surface treat almost 4 miles</li>
                <li>• Treat and repair 1.4 miles of footway surfaces</li>
                <li>• Predicted 4,400 pothole fills (3,000 roads, 1,400 footways)</li>
                <li>• Planned preventative-to-reactive ratio of around 0.7:1</li>
                <li>• Itchen Bridge: drainage renewal, 1 mile footway reconstruction, ½ mile resurfacing</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                On a 591.7km (368-mile) network, 3.5 miles of resurfacing treats under 1% of carriageway
                in a year — while 235 miles need capital funding.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Highways Partnership context</h3>
              <p className="text-amber-800 text-sm mb-3">
                Maintenance is delivered through the Southampton Highways Partnership with Balfour Beatty Living
                Places, alongside in-house teams. The council and its supplier are corporate members of the
                Local Council Roads Innovation Group (LCRIG) — contributing to its AMBER best-practice scorecard
                despite documented emergency budgeting.
              </p>
              <p className="text-amber-800 text-sm">
                The council also maintains 1,218.8km of footways, 89.3km of cycleways, 117 signal-controlled
                junctions and 42 bridges — a dense urban network under sustained traffic pressure from the port,
                hospital, schools and daily peak flows of 16,000 vehicles into the city centre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER council with GREEN spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Southampton invests above its DfT allocation — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeded DfT allocation in 2024/25 (£4.20m vs £2.40m)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> All road classes surveyed annually since GAIST alignment in 2022
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management framework linked to Connected Southampton 2040
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> LCRIG membership and risk-based inspection regime via Highways Partnership
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Legacy SCANNER B/C data showed 78–80% green through 2023
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-maintained classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — only 24% green, 66% amber, 10% red in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits roads are deteriorating each year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 0% preventative maintenance recorded in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Emergency/statutory minimum service budget for two consecutive years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 23,383 pothole and footway fills in five years on a failing network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> GAIST shows U-road Cat 1 falling from 25.68% to 16.40% since 2022
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend but deteriorating GAIST condition data, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reporting) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class and GAIST category band — was it in the 66% amber or 10% red network?</li>
              <li>• Whether the council&apos;s emergency strategy left your road for reactive patching only</li>
              <li>• Boundary check — city road (Southampton), county road (Hampshire) or trunk road (National Highways)</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Southampton&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Southampton first
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting through the Southampton Highways Partnership creates a dated council record — useful
            evidence if the defect was known before your incident, or if the council failed to repair it within
            its agreed risk-based response times. The transparency report states reactive repairs are
            &ldquo;categorised by agreed defect and risk parameters, and response times.&rdquo; Keep your
            reference number and any confirmation emails.
          </p>
          <a
            href="https://www.southampton.gov.uk/travel-transport/report-issue/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a highway issue to Southampton City Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            If the pothole is on the M27, M3 or another trunk road, report it to National Highways instead.
            Roads outside the city boundary are maintained by Hampshire County Council.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Southampton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded AMBER council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 70% capital-funding argument</li>
                <li>• No GAIST deterioration data cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 70% of roads need capital funding documented</li>
                <li>✅ Emergency minimum-service strategy cited</li>
                <li>✅ 23,383 repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Southampton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=southampton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Southampton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southampton spends above its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on whether the specific defect that
                damaged your vehicle was reasonably inspected and repaired under Section 58 — not on aggregate
                spending. Southampton is AMBER overall because road condition is AMBER: only 24% of the network
                was in good (green) condition in 2024, 66% was amber and 10% red, and the council admits roads
                are deteriorating each year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 475.6km — 80% of Southampton&apos;s 591.7km network. Since 2022 all
                U-roads are surveyed annually using GAIST 360° camera imagery. In 2024, 16.16% of U-road surface
                area was in moderate-to-severe deterioration (Category 4) and 6.09% in severe deterioration
                (Category 5). Prior CVI surveys found 8–15% of U-roads in RED condition between 2020 and 2023.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Is my pothole Hampshire County Council&apos;s responsibility or Southampton&apos;s?
              </h3>
              <p className="text-surface-600 text-sm">
                Southampton City Council maintains 591.7km of roads within the city boundary only. Hampshire
                County Council maintains roads outside the city. National Highways is responsible for motorways
                and major A-roads including the M27, M3, M271, A3(M) and sections of the A34. If your damage
                was on a trunk road, report it to National Highways — not Southampton or Hampshire.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southampton recorded 0% preventative maintenance in 2024/25 — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It is relevant context. The council&apos;s own spending table shows 0% estimated preventative
                maintenance and 52% reactive in 2024/25 — the same year it prepared an
                &ldquo;emergency/statutory minimum service&rdquo; Road Repairs Strategy because of budget
                pressures. That does not automatically prove liability for your specific defect, but it shows
                the council was prioritising reactive patching over structural renewal when your pothole may
                have formed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs fall from 6,137 to 4,247 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Southampton still filled 4,247 potholes and footway defects in 2024/25 — 2,781 on roads alone.
                The council predicts around 4,400 fills in 2025/26. The lower total partly reflects the end of
                the &ldquo;Find and Fix&rdquo; programme that contributed 1,526 and 2,045 repairs in earlier years.
                Overall condition data shows the network is still deteriorating.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southampton switched from SCANNER to GAIST surveys — can the council compare old and new data?
              </h3>
              <p className="text-surface-600 text-sm">
                Carefully. SCANNER laser surveys cover A and B/C roads through 2023; since 2022 GAIST 360°
                imagery assesses all road classes using five PAS2161-style categories. The council states this
                alignment allows funding to be allocated more consistently — but Category 4 &ldquo;moderate to
                severe&rdquo; on A-roads rose from 3.87% to 5.57% between 2022 and 2024, and overall the council
                admits &ldquo;the roads in Southampton are deteriorating each year.&rdquo;
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Do I need to report the pothole before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Reporting creates a dated council record through the Southampton Highways Partnership — useful
                if the defect was known before your incident or left unrepaired within the council&apos;s agreed
                risk-based response times. The council categorises reactive repairs by &ldquo;agreed defect and
                risk parameters, and response times.&rdquo; Keep your reference number and any confirmation emails.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.southampton.gov.uk/travel-transport/travel-transport-online-documents/local-highway-maintenance-transparency-report-june-2025/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Southampton City Council Local Highways Maintenance Transparency Report (June 2025, last updated 25 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
